import React, { useMemo } from 'react';
import Table from '@components/Table';
import Pagination from '@components/Table/Pagination';
import styled from 'styled-components';
import { parseFileSize } from '@/helpers/parsers';
import { parseTime, highlightText } from '@/helpers/deliveryHistoryUtils';
import { DeliveryItem } from './types';

const TableWrap = styled.div`
  margin-top: 40px;
`;

const SearchIndicator = styled.div`
  margin-bottom: 10px;
  padding: 8px;
  background-color: #f8f9fa;
  border-left: 4px solid #007bff;
  color: #495057;
  font-weight: 500;
`;

interface DeliveryHistoryTableProps {
  items: DeliveryItem[];
  page: number;
  setPage: (page: number) => void;
  totalPages: number;
  pageSize: number;
  pageSizeOptions: number[];
  setPageSize: (size: number) => void;
  globalSearch: string;
  deliveryId?: string;
  clientId?: string;
}

const DeliveryHistoryTable: React.FC<DeliveryHistoryTableProps> = ({
  items,
  page,
  setPage,
  totalPages,
  pageSize,
  pageSizeOptions,
  setPageSize,
  globalSearch,
  deliveryId,
  clientId
}) => {
  // Define table columns
  const historyTableColumns = useMemo(() => {
    const columns = [
      {
        key: 'date',
        label: 'Delivery Time (CT)',
        sortable: false,
        render: (row: DeliveryItem) => {
          // Format: 2022-08-01 14:32:17.229000000
          // Reformat so new Date() knows time is in UTC
          // Expected Format: 2022-08-01T14:32:17.229000000Z
          const t = row.time.split(" ").join("T").concat('', 'Z');
          const formattedDate = new Date(t).toLocaleString('en-US', { timeZone: "America/Chicago"});
          return globalSearch ? highlightText(formattedDate, globalSearch) : formattedDate;
        },
      },
      {
        key: 'file',
        label: 'File Name',
        sortable: true,
        render: (row: DeliveryItem) => {
          const date = new Date(row.time.split(" ").join("T").concat('', 'Z'));
          // Allow links 7 days in the past to be viewed
          if (date.getTime() > Date.now() - 604800000) {
            return <a href={row.url} download>{globalSearch ? highlightText(row.file, globalSearch) : row.file}</a>
          }
          else {
            return globalSearch ? highlightText(row.file, globalSearch) : row.file;
          }
        },
      },
      {
        key: 'league',
        label: 'League',
        sortable: true,
        render: (row: DeliveryItem) => globalSearch ? highlightText(row.league, globalSearch) : row.league,
      },
      {
        key: 'type',
        label: 'Type',
        sortable: true,
        render: (row: DeliveryItem) => globalSearch ? highlightText(row.type, globalSearch) : row.type,
      },
      {
        key: 'created',
        label: 'Created',
        sortable: true,
        render: (row: DeliveryItem) => {
          const t = row.created.split(" ").join("T").concat('', 'Z');
          const formattedDate = new Date(t).toLocaleString('en-US', { timeZone: "America/Chicago"});
          return globalSearch ? highlightText(formattedDate, globalSearch) : formattedDate;
        },
      },
      {
        key: 'size',
        label: 'Size',
        sortable: true,
        render: (row: DeliveryItem) => {
          const formattedSize = parseFileSize(row.size);
          return globalSearch ? highlightText(formattedSize, globalSearch) : formattedSize;
        },
      },
      {
        key: 'duration',
        label: 'Duration (s)',
        sortable: false,
        render: (row: DeliveryItem) => {
          const duration = parseInt(row.total, 10) / 1000 || "-";
          return globalSearch ? highlightText(String(duration), globalSearch) : duration;
        },
      },
      {
        key: 'process',
        label: 'Processing Time (s)',
        sortable: false,
        render: (row: DeliveryItem) => {
          const time = parseTime(row.process_time);
          return globalSearch ? highlightText(String(time), globalSearch) : time;
        },
      },
      {
        key: 'delivery_time',
        label: 'Delivery Duration (s)',
        sortable: false,
        render: (row: DeliveryItem) => {
          const time = parseTime(row.delivery_time);
          return globalSearch ? highlightText(String(time), globalSearch) : time;
        },
      },
      {
        key: 'error',
        label: 'Error Message',
        sortable: false,
        render: (row: DeliveryItem) => {
          const msg = row.msg || '-';
          return globalSearch ? highlightText(msg, globalSearch) : msg;
        },
      }
    ];

    // Add delivery column if not filtered by deliveryId
    if (!deliveryId) {
      columns.splice(1, 0, {
        key: 'delivery',
        label: 'Delivery',
        sortable: false,
        render: (row: DeliveryItem) => globalSearch ? highlightText(row.delivery || '', globalSearch) : (row.delivery || '')
      });
    }

    // Add client column if not filtered by clientId
    if (!clientId) {
      columns.splice(1, 0, {
        key: 'client',
        label: 'Client',
        sortable: true,
        render: (row: DeliveryItem) => globalSearch ? highlightText(row.client || '', globalSearch) : (row.client || ''),
      });
    }

    return columns;
  }, [globalSearch, deliveryId, clientId]);

  // Prepare table data with memoization
  const historyTableData = useMemo(() => {
    return items?.map((item: DeliveryItem) => ({
      data: {
        ...item,
        tableKey: `${item.delivery}_${item.time}`
      }
    }));
  }, [items]);

  return (
    <TableWrap>
      {items?.length > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          setPage={setPage}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          setPageSize={setPageSize}
        />
      )}
      {globalSearch && (
        <SearchIndicator>
          Showing results for global search: <strong>"{globalSearch}"</strong>
        </SearchIndicator>
      )}
      <Table columns={historyTableColumns} data={historyTableData} keyprop="tableKey" />
      {items?.length > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          setPage={setPage}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          setPageSize={setPageSize}
        />
      )}
    </TableWrap>
  );
};

export default DeliveryHistoryTable;
